import { Feature } from "@bookingsuedtirol/mss-nodejs/dist/types/response.js";
import ClassNames from "classnames";
import { FunctionComponent } from "react";
import { getActiveSite } from "../../selectors/sites.js";
import { selectAccommodationFeatures } from "../../slices/accommodation.js";
import { useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/accommodation-features-module.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import {
  AccommodationFeaturesModuleSettings,
  BaseModuleProps,
} from "../../types/index.js";
import { css } from "../../utils/emotion.js";
import { getActiveColorScheme } from "../../utils/utils.js";
import AccommodationFeatureIcon from "../AccommodationFeatureIcon.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";
import ReadMoreCollapse from "../ReadMoreCollapse.js";

type Props = BaseModuleProps<AccommodationFeaturesModuleSettings>;

const AccommodationFeaturesModule: FunctionComponent<Props> = ({
  isFirstOnPage,
  translatedModule: {
    settings: { pinnedItems, textAlign, collapsedLinesCount, maxColumns },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
    id: moduleId,
  },
  translatedModule,
}) => {
  const { ids, titles } = useAppSelector((state) =>
    selectAccommodationFeatures(state, languageId),
  );
  const pinnedFeatures = (pinnedItems.length ? pinnedItems : ids).map<Feature>(
    (id) => ({ id, title: titles[id] ?? "" }),
  );
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );

  const list = (
    <ul
      className={ClassNames(styles.AccommodationFeaturesModule__List, {
        [styles["AccommodationFeaturesModule__List--columns-3"]]:
          maxColumns === 3,
        [styles["AccommodationFeaturesModule__List--columns-4"]]:
          maxColumns === 4,
      })}
    >
      {pinnedFeatures.map(({ id, title }) => (
        <li className={styles.AccommodationFeaturesModule__ListItem} key={id}>
          <AccommodationFeatureIcon
            featureId={id}
            className={css({ fill: scheme.primary.background })}
          />
          <span>{title}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={moduleId}
      colors={{ background: scheme.main.background, color: scheme.main.text }}
    >
      <div
        className={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--default-padding"],
        )}
      >
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />

        {collapsedLinesCount !== undefined ? (
          <ReadMoreCollapse
            backgroundColor={scheme.main.background}
            languageId={languageId}
            collapsedLinesCount={collapsedLinesCount}
            isEditing={false}
          >
            {list}
          </ReadMoreCollapse>
        ) : (
          list
        )}
      </div>
    </ModuleWithHeadings>
  );
};

export default AccommodationFeaturesModule;
